import {
    Children,
    cloneElement,
    Fragment,
    isValidElement,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
    type ReactElement,
    type ReactNode,
} from 'react';
import Button, { type ButtonProps } from '~/components/new/Button';
import IconAction from '~/components/new/IconAction';
import { RemixIconNames } from '~/components/ui/Icon';
import '~/styles/components/new/box.scss';
import type { DataTestAttributes } from '~/utils/qa';
import { classNames } from '~/utils/styles';
import Divider from './Divider';

export type BoxHandler = {
    expand: (state: boolean) => Promise<void>;
};

function Box({
    reverseHeader = false,
    fullWidthContent = false,
    className = '',
    children,
    collapsible = false,
    initCollapsed = false,
    ['data-test-component']: testComponent = 'new/Box',
    ['data-test-id']: testId,
    ['data-test-name']: testName,
    ref,
}: {
    reverseHeader?: boolean;
    fullWidthContent?: boolean;
    className?: string;
    children: ReactNode;
    collapsible?: boolean;
    initCollapsed?: boolean;
    ref?: React.Ref<BoxHandler>;
} & DataTestAttributes) {
    const titleElements: ReactNode[] = [];
    const actionElements: ReactNode[] = [];
    const filterElements: ReactNode[] = [];
    const contentElements: ReactNode[] = [];
    const footerElements: ReactNode[] = [];
    const overlineElements: ReactNode[] = [];

    const extractChild = (child: ReactNode) => {
        if (!isValidElement(child)) {
            contentElements.push(child);
            return;
        }

        switch (child.type) {
            case Fragment:
                extractChild((child as ReactElement<{ children?: ReactNode }>).props.children);
                break;
            case Box.Title:
                titleElements.push(child);
                break;
            case Box.Actions:
                actionElements.push(child);
                break;
            case Box.Filters:
                filterElements.push(child);
                break;
            case Box.Footer:
                footerElements.push(child);
                break;
            case Box.Overline:
                overlineElements.push(child);
                break;
            case Box.AddOn:
            default:
                contentElements.push(child);
        }
    };

    Children.forEach(children, extractChild);

    const showActionsAndFilters =
        actionElements.length > 0 || filterElements.length > 0 || collapsible;
    const showDivider =
        actionElements.length > 0 && filterElements.length > 0 && titleElements.length > 0;
    const showHeader = titleElements.length > 0 || showActionsAndFilters;

    const [expanded, setExpanded] = useState(!initCollapsed);
    const [withTransition, setWithTransition] = useState(false);
    useEffect(() => setWithTransition(true), []);

    const API = {
        expand: (state: boolean) =>
            new Promise<void>((resolve) => {
                setExpanded(state);
                setTimeout(() => {
                    resolve();
                }, 300);
            }),
    };

    useImperativeHandle(ref, () => API);

    const collapsableContentRef = useRef<HTMLDivElement>(null!);

    useEffect(() => {
        if (!collapsible) return;

        const updateContentMaxHeight = () => {
            if (collapsableContentRef.current) {
                const scrollHeight = collapsableContentRef.current?.scrollHeight ?? 0;
                collapsableContentRef.current.style.setProperty(
                    '--box-content-max-height',
                    `${scrollHeight}px`,
                );
            }
        };

        const mutationObserver = new MutationObserver(updateContentMaxHeight);

        updateContentMaxHeight();
        if (collapsableContentRef.current) {
            mutationObserver.observe(collapsableContentRef.current, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            mutationObserver.disconnect();
        };
    }, [collapsible]);

    return (
        <div
            className={classNames({
                [className]: Boolean(className),
                'box-wrapper': true,
                'gap-2': overlineElements.length > 0,
            })}
            style={{
                '--box-content-max-height': collapsible
                    ? `${collapsableContentRef.current?.scrollHeight ?? 0}px`
                    : undefined,
            }}
            data-test-component={testComponent}
            data-test-id={testId}
            data-test-name={testName}>
            {overlineElements.length > 0 && (
                <div className='box-overline-wrapper'>{overlineElements}</div>
            )}
            <div
                className={classNames({
                    box: true,
                    collapsed: collapsible && !expanded,
                })}>
                {showHeader && (
                    <div
                        className={classNames({
                            'box-header': true,
                            'flex-row-reverse': reverseHeader,
                            collapsed: collapsible && !expanded,
                        })}
                        data-test-component='new/Box/Header'>
                        {titleElements}
                        {showActionsAndFilters && (
                            <div
                                className={classNames({
                                    'box-header-actions-n-filters': true,
                                    expands: titleElements.length === 0,
                                })}
                                data-test-component='new/Box/Header/ActionsAndFilters'>
                                {filterElements}
                                {showDivider && <Divider vertical />}
                                {actionElements}
                                {collapsible && (
                                    <Button
                                        size='medium'
                                        variant='filled-light'
                                        onClick={() => void API.expand(!expanded)}
                                        icon={
                                            expanded
                                                ? RemixIconNames.RiArrowUpSLine
                                                : RemixIconNames.RiArrowDownSLine
                                        }
                                        data-test-component='new/Box/Header/ActionsAndFilters/ShowHide'
                                        data-test-name={expanded ? 'hide' : 'show'}
                                        name='collapse-handler'
                                    />
                                )}
                            </div>
                        )}
                    </div>
                )}

                <div
                    ref={collapsableContentRef}
                    inert={!expanded}
                    className={classNames({
                        'box-content': true,
                        headerless: !showHeader,
                        'full-width': fullWidthContent,
                        collapsible: collapsible && withTransition,
                        collapsed: collapsible && !expanded,
                    })}>
                    {contentElements}
                </div>

                {footerElements}
            </div>
        </div>
    );
}

function Title({
    extra,
    children,
    className = '',
    info,
}: {
    extra?: string;
    children: ReactNode;
    className?: string;
    info?: string;
}) {
    return (
        <div className={`box-header-title ${className}`}>
            <div className='box-header-title-text'>{children}</div>
            {extra && <span className='box-header-title-extra'>{extra}</span>}
            {info && (
                <IconAction
                    icon={RemixIconNames.RiInformationLine}
                    colorVar='core-tertiary'
                    title={info}
                />
            )}
        </div>
    );
}

type ButtonsProps = ButtonProps;
function Actions({ children }: { children: ReactNode }) {
    const iterableChildren = Array.isArray(children) ? children : [children];
    return (
        <div className='box-header-actions'>
            {iterableChildren.map((child, idx) =>
                child && child.type === Button
                    ? cloneElement(child as ReactElement<ButtonProps, typeof Button>, {
                          size: 'medium',
                          key: idx,
                      })
                    : child,
            )}
        </div>
    );
}

function Filters({
    children,
}: {
    children:
        ReactElement<ButtonsProps, typeof Button> | ReactElement<ButtonsProps, typeof Button>[];
}) {
    return (
        <div className='box-header-filters'>
            {Array.isArray(children)
                ? children.map((child) => cloneElement(child, { size: 'medium' }))
                : cloneElement(children, { size: 'medium' })}
        </div>
    );
}

function AddOn({
    children,
    align,
    direction = 'column',
    divided = true,
}: {
    children: ReactNode;
    align?: 'start' | 'center' | 'end';
    direction?: 'row' | 'column';
    divided?: boolean | 'top' | 'bottom';
}) {
    return (
        <div
            className={classNames({
                [`box-addon items-${align ?? 'between'} flex-${direction}`]: true,
                [`box-addon-divided`]: Boolean(divided),
                [`box-addon-divided-${divided}`]: typeof divided === 'string',
            })}>
            {children}
        </div>
    );
}

function Footer({
    children,
    divided = false,
    className = '',
}: {
    children: ReactNode;
    divided?: boolean;
    className?: string;
}) {
    return (
        <div className={`box-footer ${divided ? 'box-footer-divided' : ''} ${className}`}>
            {children}
        </div>
    );
}
function Overline({ children }: { children: ReactNode }) {
    return <div className='box-overline'>{children}</div>;
}

Box.Title = Title;
Box.Actions = Actions;
Box.Filters = Filters;
Box.AddOn = AddOn;
Box.Footer = Footer;
Box.Overline = Overline;

export default Box;
